import React, { useEffect, useState, useCallback } from 'react';
import { Card, Button, Modal, Form, Badge, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchProviders, createProvider, updateProvider } from '../../services/adminApi';
import { useAuth } from '../../services/AuthContext';

function ConfirmModal({ show, onHide, onConfirm, title, message, confirmLabel, variant = 'warning' }) {
    const [busy, setBusy] = useState(false);
    const handleConfirm = async () => {
        setBusy(true);
        try { await onConfirm(); onHide(); }
        finally { setBusy(false); }
    };
    return (
        <Modal show={show} onHide={onHide} centered size="sm">
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: 15 }}>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ fontSize: 13 }}>{message}</Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" size="sm" onClick={onHide} disabled={busy}>Cancel</Button>
                <Button variant={variant} size="sm" onClick={handleConfirm} disabled={busy}>
                    {busy && <Spinner size="sm" animation="border" className="me-1" />}
                    {confirmLabel}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

const EMPTY_FORM = { code: '', name: '', description: '', website_url: '' };

function ProviderModal({ show, onHide, onSave, initial }) {
    const { can } = useAuth();
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const editing = !!initial;

    useEffect(() => {
        if (show) {
            setForm(initial ? { ...initial, description: initial.description || '', website_url: initial.website_url || '' } : EMPTY_FORM);
            setError(null);
        }
    }, [show, initial]);

    const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

    const handleSave = async () => {
        if (!form.name.trim()) { setError('Name is required.'); return; }
        if (!editing && !form.code.trim()) { setError('Code is required.'); return; }
        setSaving(true);
        setError(null);
        try {
            const payload = {
                name: form.name.trim(),
                description: form.description?.trim() || null,
                website_url: form.website_url?.trim() || null,
            };
            if (!editing) payload.code = form.code.trim().toUpperCase();
            await onSave(payload);
            onHide();
        } catch (e) {
            setError(e.response?.data?.detail || 'Save failed.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: 16 }}>{editing ? 'Edit Provider' : 'New Fund Provider'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger" className="py-2" style={{ fontSize: 13 }}>{error}</Alert>}
                <Form>
                    {!editing && (
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Code</Form.Label>
                            <Form.Control size="sm" data-testid="provider-code" value={form.code}
                                onChange={e => set('code', e.target.value.toUpperCase())}
                                placeholder="e.g. VANGUARD" maxLength={32} />
                            <Form.Text className="text-muted">Short uppercase identifier used in fund IDs. Cannot be changed later.</Form.Text>
                        </Form.Group>
                    )}
                    <Form.Group className="mb-3">
                        <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Name</Form.Label>
                        <Form.Control size="sm" data-testid="provider-name" value={form.name} onChange={e => set('name', e.target.value)}
                            placeholder="e.g. Vanguard Asset Management" />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Description <span className="text-muted fw-normal">(optional)</span></Form.Label>
                        <Form.Control as="textarea" rows={2} size="sm" value={form.description}
                            onChange={e => set('description', e.target.value)}
                            placeholder="Brief description of the provider" />
                    </Form.Group>
                    <Form.Group className="mb-1">
                        <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Website <span className="text-muted fw-normal">(optional)</span></Form.Label>
                        <Form.Control size="sm" value={form.website_url} onChange={e => set('website_url', e.target.value)}
                            placeholder="https://www.example.com" />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" size="sm" onClick={onHide} disabled={saving}>Cancel</Button>
                <Button variant="primary" size="sm" onClick={handleSave} disabled={saving || !can.editCatalogue()}
                        title={can.editCatalogue() ? undefined : "Requires the waive-config role"}>
                    {saving && <Spinner size="sm" animation="border" className="me-1" />}
                    {editing ? 'Save Changes' : 'Create Provider'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

function Providers() {
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [confirm, setConfirm] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        fetchProviders()
            .then(data => setProviders(data.providers || []))
            .catch(() => setError('Failed to load providers.'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => { setEditing(null); setModalOpen(true); };
    const openEdit = (p) => { setEditing(p); setModalOpen(true); };

    const handleSave = async (payload) => {
        if (editing) {
            await updateProvider(editing.id, payload);
        } else {
            await createProvider(payload);
        }
        load();
    };

    const handleToggleActive = async () => {
        await updateProvider(confirm.provider.id, { active: confirm.nextActive });
        setConfirm(null);
        load();
    };

    return (
        <div className="page-content">
            <div className="page-header">
                <div>
                    <h1 className="page-title">Fund Providers</h1>
                    <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                        Fund management companies whose funds appear in the global catalogue.
                        Distinct from tenants — a provider may or may not be a Waive commercial client.
                    </p>
                </div>
                <Button variant="primary" size="sm" onClick={openCreate}>
                    <FontAwesomeIcon icon="plus" className="me-2" />New Provider
                </Button>
            </div>

            {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

            <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white border-bottom py-2">
                    <span style={{ fontSize: 13, fontWeight: 600 }}>
                        {providers.length} provider{providers.length !== 1 ? 's' : ''}
                    </span>
                </Card.Header>
                <Card.Body className="p-0">
                    {loading ? (
                        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
                    ) : (
                        <table className="table table-hover mb-0" style={{ fontSize: 13 }}>
                            <thead className="table-light">
                                <tr>
                                    <th className="px-4 py-3 fw-medium" style={{ color: '#64748b' }}>Code</th>
                                    <th className="px-4 py-3 fw-medium" style={{ color: '#64748b' }}>Name</th>
                                    <th className="px-4 py-3 fw-medium" style={{ color: '#64748b' }}>Description</th>
                                    <th className="px-4 py-3 fw-medium text-end" style={{ color: '#64748b' }}>Funds</th>
                                    <th className="px-4 py-3 fw-medium" style={{ color: '#64748b' }}>Status</th>
                                    <th className="px-4 py-3 fw-medium" style={{ color: '#64748b' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                            {providers.map(p => (
                                <tr key={p.id}>
                                    <td className="px-4 py-3 fw-semibold" style={{ fontFamily: 'monospace' }}>{p.code}</td>
                                    <td className="px-4 py-3 fw-medium">{p.name}</td>
                                    <td className="px-4 py-3 text-muted" style={{ maxWidth: 280 }}>
                                        <span style={{ overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical' }}>
                                            {p.description || '—'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-end">
                                        <span className="badge bg-light text-dark border" style={{ fontSize: 12 }}>
                                            {p.fund_count}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <Badge bg={p.active ? 'success' : 'secondary'} style={{ fontSize: 11 }}>
                                            {p.active ? 'Active' : 'Inactive'}
                                        </Badge>
                                    </td>
                                    <td className="px-4 py-3">
                                        <Button variant="outline-secondary" size="sm" className="me-2" onClick={() => openEdit(p)}>
                                            <FontAwesomeIcon icon="pen" />
                                        </Button>
                                        <Button variant={p.active ? 'outline-warning' : 'outline-success'} size="sm"
                                            onClick={() => setConfirm({ provider: p, nextActive: !p.active })}>
                                            <FontAwesomeIcon icon={p.active ? 'ban' : 'check'} />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                            {providers.length === 0 && (
                                <tr><td colSpan={6} className="text-center text-muted py-5">No providers found</td></tr>
                            )}
                            </tbody>
                        </table>
                    )}
                </Card.Body>
            </Card>

            <ProviderModal
                show={modalOpen}
                onHide={() => setModalOpen(false)}
                onSave={handleSave}
                initial={editing}
            />

            {confirm && (
                <ConfirmModal
                    show
                    onHide={() => setConfirm(null)}
                    onConfirm={handleToggleActive}
                    title={confirm.nextActive ? 'Activate provider' : 'Deactivate provider'}
                    message={confirm.nextActive
                        ? `Activate "${confirm.provider.name}"?`
                        : `Deactivate "${confirm.provider.name}"? Its funds will remain in the catalogue but the provider will be marked inactive.`}
                    confirmLabel={confirm.nextActive ? 'Activate' : 'Deactivate'}
                    variant={confirm.nextActive ? 'success' : 'warning'}
                />
            )}
        </div>
    );
}

export default Providers;
